import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Clock, Calendar, User, Users, Settings,
  MapPin, BarChart3, LogOut, Menu, X, Building2, LayoutGrid,
  CalendarDays, CalendarCheck
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', roles: ['employee', 'manager', 'admin'] },
  { to: '/clock', icon: Clock, label: 'Pointage', roles: ['employee', 'manager', 'admin'] },
  { to: '/my-attendance', icon: Calendar, label: 'Mes Présences', roles: ['employee', 'manager', 'admin'] },
  { to: '/leaves', icon: CalendarDays, label: 'Mes Congés', roles: ['employee', 'manager', 'admin'] },
  { to: '/profile', icon: User, label: 'Mon Profil', roles: ['employee', 'manager', 'admin'] },
  { divider: true },
  { to: '/admin/menu', icon: LayoutGrid, label: 'Menu Admin', roles: ['admin', 'manager'] },
  { to: '/admin', icon: BarChart3, label: 'Vue Équipe', roles: ['manager', 'admin'] },
  { to: '/admin/employees', icon: Users, label: 'Employés', roles: ['admin'] },
  { to: '/admin/teams', icon: Building2, label: 'Équipes', roles: ['admin', 'manager'] },
  { to: '/admin/reports', icon: BarChart3, label: 'Rapports', roles: ['admin', 'manager'] },
  { to: '/admin/leaves', icon: CalendarCheck, label: 'Congés (validation)', roles: ['admin', 'manager'] },
  { to: '/admin/locations', icon: MapPin, label: 'Localisations', roles: ['admin'] },
  { to: '/admin/settings', icon: Settings, label: 'Paramètres', roles: ['admin'] },
];

/* Palette GIPER */
const G = {
  ink:'#0A1628', mahog:'#1A2B4A', navy3:'#243660',
  gold:'#C9A84C', goldLt:'#E8CC7A', goldDk:'#9B7B2E',
  bg:'#F5F7FA', card:'#FFFEF9', parch:'#EDF0F7',
  border:'#D0D8EC', muted:'#6B7A99', text2:'#8597B5',
  dng:'#CE1126',
  headerBg:'linear-gradient(135deg,#0A1628 0%,#1A2B4A 50%,#243660 100%)',
  serif:"'Cinzel',serif", serif2:"'Cormorant Garamond',serif", body:"'Raleway',sans-serif",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleLogout = async () => { await logout(); navigate('/login'); };
  const filteredNav = navItems.filter(item => item.divider || item.roles.includes(user?.role));

  const SidebarContent = () => (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:G.card,borderRight:`1px solid ${G.border}`,fontFamily:G.body}}>
      <nav style={{flex:1,overflowY:'auto',padding:'14px 10px',scrollbarWidth:'none'}}>
        <div style={{fontFamily:G.serif,fontSize:10,letterSpacing:'2px',textTransform:'uppercase',color:G.text2,padding:'6px 13px 8px'}}>Navigation</div>
        {filteredNav.map((item, i) => {
          if (item.divider) return (
            <div key={i} style={{fontFamily:G.serif,fontSize:10,letterSpacing:'2px',textTransform:'uppercase',color:G.text2,padding:'16px 13px 6px',borderTop:`1px solid ${G.border}`,marginTop:10}}>Administration</div>
          );
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} end={item.to==='/admin'}
              onClick={() => setSidebarOpen(false)}
              style={({isActive}) => ({
                display:'flex',alignItems:'center',gap:12,padding:'10px 13px',
                borderRadius:10,cursor:'pointer',transition:'all .18s',
                whiteSpace:'nowrap',marginBottom:2,textDecoration:'none',
                position:'relative',
                background:isActive?G.parch:'transparent',
                color:isActive?G.ink:G.muted,
                fontSize:13.5,fontWeight:isActive?600:500,
                boxShadow:isActive?`inset 3px 0 0 0 ${G.gold}`:'none',
              })}>
              <Icon style={{width:17,height:17,flexShrink:0,color:'inherit'}} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div style={{padding:'12px 10px',borderTop:`1px solid ${G.border}`,flexShrink:0}}>
        <NavLink to="/profile" onClick={() => setSidebarOpen(false)}
          style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:10,background:G.parch,border:`1px solid ${G.border}`,textDecoration:'none',transition:'all .2s'}}>
          {user?.avatar
            ? <img src={user.avatar} alt="" style={{width:34,height:34,borderRadius:'50%',objectFit:'cover',border:`2px solid ${G.gold}`}} />
            : <div style={{width:34,height:34,borderRadius:'50%',background:`linear-gradient(135deg,${G.gold},${G.goldDk})`,color:G.ink,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:12,flexShrink:0}}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
          }
          <div style={{flex:1,minWidth:0}}>
            <p style={{color:G.ink,fontSize:12.5,fontWeight:600,margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user?.firstName} {user?.lastName}</p>
            <p style={{color:G.muted,fontSize:10.5,margin:0,textTransform:'capitalize'}}>{user?.role}</p>
          </div>
        </NavLink>
        <button onClick={handleLogout}
          style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',width:'100%',borderRadius:10,border:'none',background:'transparent',color:G.muted,fontSize:12.5,cursor:'pointer',marginTop:4,transition:'all .2s',fontFamily:G.body}}
          onMouseEnter={e=>{e.currentTarget.style.background='rgba(206,17,38,.08)';e.currentTarget.style.color=G.dng;}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color=G.muted;}}>
          <LogOut style={{width:16,height:16}} />
          Déconnexion
        </button>
        <div style={{marginTop:10,padding:'10px 12px 2px',borderTop:`1px solid ${G.border}`,display:'flex',alignItems:'center',gap:8,fontSize:10,color:G.text2}}>
          <span style={{width:7,height:7,borderRadius:'50%',background:'#1f9d61',display:'inline-block'}}></span>
          OGP · Système de pointage
        </div>
      </div>
    </div>
  );

  const Masthead = () => (
    <div style={{flexShrink:0}}>
      <div className="flag-stripe" />
      <div className="republic">
        <span className="r">République de Guinée — Travail · Justice · Solidarité</span>
        <span style={{letterSpacing:'.5px',textTransform:'none'}}>Office Guinéen de Publicité</span>
      </div>
      <div style={{background:G.headerBg,borderBottom:'1px solid rgba(201,168,76,.25)',display:'flex',alignItems:'center',gap:16,padding:'12px 20px'}}>
        <button className="lg:hidden" onClick={() => setSidebarOpen(true)}
          style={{background:'none',border:'none',cursor:'pointer',color:'#cdd6e8',padding:0,display:'flex'}}>
          <Menu style={{width:22,height:22}} />
        </button>
        <div style={{height:46,display:'flex',alignItems:'center',background:'#fff',borderRadius:11,border:'1px solid rgba(201,168,76,.35)',padding:'6px 12px',boxShadow:'0 4px 14px rgba(0,0,0,.2)',flexShrink:0}}>
          <img src="/logo-ogp.png" alt="OGP" style={{height:'100%',width:'auto',objectFit:'contain',display:'block'}} />
        </div>
        <div>
          <b style={{fontFamily:G.serif,color:'#fff',fontSize:17,fontWeight:700,letterSpacing:'.5px',display:'block',lineHeight:1.15}}>OGP PRÉSENCE</b>
          <span style={{fontFamily:G.body,color:G.goldLt,fontSize:9.5,letterSpacing:'2.5px',textTransform:'uppercase'}}>Système de pointage intelligent</span>
        </div>
        <div className="hidden lg:flex" style={{marginLeft:'auto',alignItems:'center',gap:10}}>
          <span style={{fontFamily:G.serif2,fontStyle:'italic',color:'#b9c4dc',fontSize:14}}>
            {user?.firstName} {user?.lastName}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100vh',background:G.bg,fontFamily:G.body,color:G.ink}}>
      <Masthead />
      <div style={{flex:1,display:'flex',overflow:'hidden'}}>
        <aside className="hidden lg:flex" style={{width:250,flexShrink:0,flexDirection:'column'}}>
          <SidebarContent />
        </aside>
        {sidebarOpen && (
          <div style={{position:'fixed',inset:0,zIndex:50,display:'flex'}}>
            <div style={{position:'fixed',inset:0,background:'rgba(10,22,40,.55)',backdropFilter:'blur(6px)'}} onClick={() => setSidebarOpen(false)} />
            <aside style={{position:'relative',width:260,display:'flex',flexDirection:'column',zIndex:10}}>
              <button onClick={() => setSidebarOpen(false)} style={{position:'absolute',top:14,right:14,background:'none',border:'none',color:G.muted,cursor:'pointer',zIndex:11}}>
                <X style={{width:20,height:20}} />
              </button>
              <SidebarContent />
            </aside>
          </div>
        )}
        <main style={{flex:1,overflow:'auto',background:G.bg}}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
